import React, { useEffect, useState, useCallback } from "react";
import {
  maintenanceApi,
  StationDropdownItem,
  OrgItem,
  PricingOverrideItem,
  ServiceTypeWithTaxes,
} from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import PricingOverrideForm from "./PricingOverrideForm";
import { Search, Plus, Ban, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { useNotice } from "../shared/useNotice";

const PricingManagement: React.FC = () => {
  const [stations, setStations] = useState<StationDropdownItem[]>([]);
  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeWithTaxes[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<number | "">("");
  const [selectedOrgId, setSelectedOrgId] = useState<number | "">("");
  const [overrides, setOverrides] = useState<PricingOverrideItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingOverride, setEditingOverride] = useState<PricingOverrideItem | null>(null);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");
  const [expandedTax, setExpandedTax] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      maintenanceApi.getStationsDropdown(),
      maintenanceApi.getOrgs(),
      maintenanceApi.getServiceTypes(),
    ]).then(([stationsRes, orgsRes, stRes]) => {
      setStations(stationsRes.data);
      setOrgs(orgsRes.data);
      setServiceTypes(stRes.data);
    }).catch(console.error);
  }, []);

  const fetchOverrides = useCallback(async () => {
    if (!selectedStationId && !selectedOrgId) {
      setOverrides([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (selectedStationId) {
        const res = await maintenanceApi.getStationPricingOverrides(selectedStationId as number);
        setOverrides(res.data);
      } else if (selectedOrgId) {
        const res = await maintenanceApi.getOrgPricingOverrides(selectedOrgId as number);
        setOverrides(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch overrides:", err);
      setError("Failed to load pricing overrides");
    } finally {
      setLoading(false);
    }
  }, [selectedStationId, selectedOrgId]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const handleSetActive = async (id: number, isActive: boolean) => {
    if (!isActive && !window.confirm(
      "Disable this override? Overrides are immutable — to change a price, disable this one and add a new override."
    )) return;
    try {
      await maintenanceApi.setPricingOverrideActive(id, isActive);
      setMessage(isActive ? "Override enabled" : "Override disabled");
      fetchOverrides();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update override");
    }
  };

  const handleFormDone = () => {
    setShowForm(false);
    setEditingOverride(null);
    setMessage("Override created");
    fetchOverrides();
  };

  const handleAdd = () => {
    setEditingOverride(null);
    setShowForm(true);
  };

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleString();
    } catch {
      return d;
    }
  };

  const defaults = overrides.filter((o) => o.is_default);
  const timeBounded = overrides.filter((o) => !o.is_default);

  return (
    <MaintenanceLayout title="Pricing Management">
      <div className="maint-stations">
        {/* Service Types Reference */}
        <div className="maint-detail-card" style={{ marginBottom: "16px" }}>
          <h4 style={{ marginBottom: "8px" }}>Service Types & Tax Config</h4>
          <table className="maint-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Tax Model</th>
                <th>Active</th>
                <th>Tax Components</th>
              </tr>
            </thead>
            <tbody>
              {serviceTypes.map((st) => (
                <tr key={st.id}>
                  <td><code>{st.service_type_key}</code></td>
                  <td>{st.tax_model}</td>
                  <td>{st.is_active ? "Yes" : "No"}</td>
                  <td>
                    {st.tax_components.length > 0 ? (
                      <span
                        style={{ cursor: "pointer", color: "#5554d3" }}
                        onClick={() => setExpandedTax(expandedTax === st.service_type_key ? null : st.service_type_key)}
                      >
                        {st.tax_components.length} components{" "}
                        {expandedTax === st.service_type_key ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </span>
                    ) : (
                      "None"
                    )}
                    {expandedTax === st.service_type_key && (
                      <div style={{ marginTop: "6px", fontSize: "13px", color: "#666" }}>
                        {st.tax_components.map((tc) => (
                          <div key={tc.id}>
                            {tc.display_name}: {tc.tax_percentage}%{" "}
                            <span style={{ color: "#999", fontSize: "12px" }}>
                              ({tc.supply_type === "inter" ? "inter-state" : "intra-state"})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Filter by station or org */}
        <div className="maint-toolbar" style={{ gap: "12px" }}>
          <label style={{ flex: 1 }}>
            <span style={{ fontSize: "13px", fontWeight: 600 }}>Filter by Station</span>
            <select
              value={selectedStationId}
              onChange={(e) => {
                setSelectedStationId(e.target.value ? Number(e.target.value) : "");
                setSelectedOrgId("");
              }}
            >
              <option value="">Select station...</option>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.org_name})</option>
              ))}
            </select>
          </label>
          <span style={{ padding: "20px 8px 0", color: "#999" }}>or</span>
          <label style={{ flex: 1 }}>
            <span style={{ fontSize: "13px", fontWeight: 600 }}>Filter by Organization</span>
            <select
              value={selectedOrgId}
              onChange={(e) => {
                setSelectedOrgId(e.target.value ? Number(e.target.value) : "");
                setSelectedStationId("");
              }}
            >
              <option value="">Select org...</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>
          {canEditMaintenance() && (
            <button
              className="maint-btn-primary"
              onClick={handleAdd}
              style={{ alignSelf: "flex-end" }}
              disabled={!selectedStationId && !selectedOrgId}
            >
              <Plus size={16} /> Add Override
            </button>
          )}
        </div>

        {message && <p className="maint-success">{message}</p>}
        {error && <p className="maint-form-error">{error}</p>}

        {showForm && (
          <PricingOverrideForm
            stations={stations}
            orgs={orgs}
            serviceTypes={serviceTypes}
            editingOverride={editingOverride}
            defaultStationId={selectedStationId ? (selectedStationId as number) : undefined}
            defaultOrgId={selectedOrgId ? (selectedOrgId as number) : undefined}
            onClose={() => { setShowForm(false); setEditingOverride(null); }}
            onDone={handleFormDone}
          />
        )}

        {loading ? (
          <p className="maint-loading">Loading...</p>
        ) : (selectedStationId || selectedOrgId) ? (
          <>
            {/* Default overrides */}
            {defaults.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <h4 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>Default Overrides (always active)</h4>
                <table className="maint-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Station</th>
                      <th>Type</th>
                      <th>Price/kWh</th>
                      <th>Service Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defaults.map((o) => (
                      <tr key={o.id}>
                        <td>{o.org_name}</td>
                        <td>{o.station_name}</td>
                        <td>{o.charger_type}</td>
                        <td style={{ fontWeight: 600 }}>&#8377;{o.price_per_kwh}</td>
                        <td><code style={{ fontSize: "12px" }}>{o.service_type_key}</code></td>
                        <td>
                          {canEditMaintenance() && (
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              {o.is_active ? (
                                <button className="maint-btn-small" onClick={() => handleSetActive(o.id, false)} style={{ color: "#c62828" }} title="Disable — to change a price, disable and add a new override"><Ban size={14} /></button>
                              ) : (
                                <>
                                  <span style={{ fontSize: "12px", color: "#999" }}>disabled</span>
                                  <button className="maint-btn-small" onClick={() => handleSetActive(o.id, true)} title="Re-enable"><RotateCcw size={14} /></button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Time-bounded overrides */}
            {timeBounded.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <h4 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>Time-Bounded Overrides</h4>
                <table className="maint-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Station</th>
                      <th>Type</th>
                      <th>Price/kWh</th>
                      <th>Valid From</th>
                      <th>Valid Thru</th>
                      <th>Priority</th>
                      <th>Service Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeBounded.map((o) => (
                      <tr key={o.id}>
                        <td>{o.org_name}</td>
                        <td>{o.station_name}</td>
                        <td>{o.charger_type}</td>
                        <td style={{ fontWeight: 600 }}>&#8377;{o.price_per_kwh}</td>
                        <td style={{ fontSize: "13px" }}>{formatDate(o.valid_from)}</td>
                        <td style={{ fontSize: "13px" }}>{formatDate(o.valid_thru)}</td>
                        <td>{o.priority}</td>
                        <td><code style={{ fontSize: "12px" }}>{o.service_type_key}</code></td>
                        <td>
                          {canEditMaintenance() && (
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              {o.is_active ? (
                                <button className="maint-btn-small" onClick={() => handleSetActive(o.id, false)} style={{ color: "#c62828" }} title="Disable — to change a price, disable and add a new override"><Ban size={14} /></button>
                              ) : (
                                <>
                                  <span style={{ fontSize: "12px", color: "#999" }}>disabled</span>
                                  <button className="maint-btn-small" onClick={() => handleSetActive(o.id, true)} title="Re-enable"><RotateCcw size={14} /></button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {overrides.length === 0 && (
              <p className="maint-empty" style={{ marginTop: "16px" }}>No pricing overrides found</p>
            )}
          </>
        ) : (
          <p className="maint-empty" style={{ marginTop: "16px" }}>Select a station or organization to view pricing overrides</p>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default PricingManagement;
